import React from 'react';

/**
 * Podium-style top-3 leaderboard display.
 * 1st place in center (tallest), 2nd on left, 3rd on right.
 * @param {{ students: any[], getLeagueColor: (league: string) => string }} props
 */

const PODIUM_COLORS = [
  { bg: '#F5A623', bar: '#F5A623', badge: '🏆' }, // 1st - Gold
  { bg: '#9B9B9B', bar: '#9B9B9B', badge: '🥈' }, // 2nd - Silver
  { bg: '#CD7F32', bar: '#CD7F32', badge: '🥉' }, // 3rd - Bronze
];

const PODIUM_HEIGHTS = ['120px', '90px', '70px']; // 1st, 2nd, 3rd bar heights

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function TopStudentsGrid({ students, getLeagueColor }) {
  if (!students.length) return null;

  // Reorder: [2nd, 1st, 3rd] for podium visual layout
  const podiumOrder = [students[1], students[0], students[2]].filter(Boolean);
  const originalIndices = [1, 0, 2]; // maps podiumOrder position to original rank index

  return (
    <div className="podium-section">
      <div className="podium-container">
        {podiumOrder.map((student, i) => {
          const rankIndex = originalIndices[i]; // 0=1st, 1=2nd, 2=3rd
          const color = PODIUM_COLORS[rankIndex];
          const barHeight = PODIUM_HEIGHTS[rankIndex];
          const isFirst = rankIndex === 0;

          return (
            <div
              key={student.user_id}
              className={`podium-entry ${isFirst ? 'podium-first' : ''}`}
            >
              {/* Avatar circle */}
              <div
                className="podium-avatar"
                style={{ borderColor: color.bg }}
              >
                <span className="podium-avatar-text" style={{ color: color.bg }}>
                  {getInitials(student.profiles.full_name)}
                </span>
                <span className="podium-badge-emoji">{color.badge}</span>
              </div>

              {/* Name + Score */}
              <p className="podium-name">{student.profiles.full_name || 'Unknown'}</p>
              <p className="podium-score">{student.score?.toLocaleString()} pts</p>

              {/* Podium bar */}
              <div
                className="podium-bar"
                style={{
                  height: barHeight,
                  background: `linear-gradient(180deg, ${color.bar} 0%, ${color.bar}CC 100%)`,
                }}
              >
                <span className="podium-rank-label">
                  {rankIndex === 0 ? '1ST' : rankIndex === 1 ? '2ND' : '3RD'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: keep original card layout as fallback */}
      <div className="top3-cards-desktop">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4 items-end">
          {students.map((student, index) => (
            <div
              key={student.user_id}
              className={`bg-white p-6 rounded-xl shadow-md text-center relative transition-all duration-300
                ${index === 0 ? 'border-2 border-indigo-500 shadow-lg scale-105' : ''}
                ${index === 1 ? 'order-first' : ''}
                ${index === 2 ? 'order-last' : ''}
              `}
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-bold text-lg">
                  {student.profiles.full_name ? student.profiles.full_name[0].toUpperCase() : '?'}
                </div>
              </div>
              <div className="absolute top-2 left-2 bg-indigo-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                {student.rank}
              </div>
              <h3 className="font-bold text-lg text-gray-900">{student.profiles.full_name || 'Unknown'}</h3>
              <p className="text-2xl font-semibold text-gray-800">{student.score} Pts</p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${getLeagueColor(
                  student.league
                )}`}
              >
                {student.league}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TopStudentsGrid;
